"use client";

import type { AudienceToolsComponent } from "../../types";
import { JSX } from "react";
import { User, Building2, Megaphone, Landmark, FlaskConical, GraduationCap } from "lucide-react";
import { RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { GuardedLink } from "../../runtime/DevPlaceholder";
import { t } from "../../i18n/strings";
import { resolveLinkHref } from "../../utils/links";
import { AudienceToolsSearchForm } from "./AudienceToolsSearchForm";

/**
 * Two-column blue band on the home page:
 *   LEFT  — site search input ("Rechercher dans le site").
 *   RIGHT — either a popular-searches chip cloud (when CMS provides
 *           `popularSearches`) OR the legacy audience-icon list (default).
 *
 * The right column is fully data-driven so editors can switch presentation
 * without code: ship `popularSearches` + `rightColumnTitle: "Les termes les
 * plus recherchés"` and the chip cloud takes over.
 */
export function AudienceToolsRenderer({ component }: { component: AudienceToolsComponent }) {
	const { searchPlaceholder, audiences, rightColumnTitle, popularSearches } = component.content;
	const showPopular = Array.isArray(popularSearches) && popularSearches.length > 0;
	const rightTitle = (rightColumnTitle ?? "").trim() || (showPopular ? "Les termes les plus recherchés" : "Informations destinées aux");

	return (
		<section className="mx-[calc(50%-50vw)] bg-[#005CA9] pt-10 pb-20 text-white">
			<div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 md:px-8 md:grid-cols-2">
				<SearchColumn placeholder={searchPlaceholder} />
				<RightColumn title={rightTitle}>
					{showPopular ? (
						<PopularSearchesCloud terms={popularSearches!} />
					) : (
						<AudienceList audiences={audiences} />
					)}
				</RightColumn>
			</div>
		</section>
	);
}

/** LEFT column — site search input with magnifier button.
 *  The interactive form (state + submit → /search?q=…) lives in
 *  {@link AudienceToolsSearchForm}, the only piece of this widget that
 *  needs to run on the client. */
function SearchColumn({ placeholder }: { placeholder: string }) {
	const locale = useLocale();
	return (
		<div className="md:flex md:flex-col md:justify-center">
			<h4 className="text-white text-[24px] font-normal leading-[127.5%]" style={{ fontFamily: "Aleo" }}>
				{t(locale, "searchSite")}
			</h4>
			<AudienceToolsSearchForm placeholder={placeholder} />
		</div>
	);
}

/** RIGHT column shell — header + slot. Children decide the body. */
function RightColumn({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<div>
			<h4 className="text-white text-[24px] font-normal leading-[127.5%]" style={{ fontFamily: "Aleo" }}>
				{title}
			</h4>
			<div className="mt-3">{children}</div>
		</div>
	);
}

const ICON_MAP: Record<string, JSX.Element> = {
	User: <User className="h-4 w-4" />,
	Building2: <Building2 className="h-4 w-4" />,
	Megaphone: <Megaphone className="h-4 w-4" />,
	Landmark: <Landmark className="h-4 w-4" />,
	FlaskConical: <FlaskConical className="h-4 w-4" />,
	GraduationCap: <GraduationCap className="h-4 w-4" />,
};

/** Legacy right column — icon-prefixed audience pills. Kept so existing
 *  payloads that still ship `audiences` continue to render unchanged. Pills whose
 *  destination isn't connected open the dev modal in preview and vanish in production. */
function AudienceList({ audiences }: { audiences: { icon: string; label: string; href: any }[] }) {
	return (
		<div className="flex flex-wrap gap-3 md:gap-4">
			{audiences.map((a) => (
				<GuardedLink
					key={a.label}
					label={a.label}
					className="inline-flex items-center gap-3 rounded-sm border border-white/30 bg-white/5 px-4 py-2 text-sm xl:text-base font-semibold hover:bg-white/10"
					href={a.href}
				>
					{ICON_MAP[a.icon]}
					{a.label}
				</GuardedLink>
			))}
		</div>
	);
}

/** New right column — chip cloud of popular search terms. Each chip links
 *  to /search?q=<label> by default; CMS can override per term via `href`. */
function PopularSearchesCloud({
	terms,
}: {
	terms: { label: string; href?: any }[];
}) {
	return (
		<div className="flex flex-wrap gap-2">
			{terms.map((term, index) => {
				const href = resolveLinkHref(term.href) || `/search?q=${encodeURIComponent(term.label)}`;
				return (
					<RuntimeLink
						key={`${term.label}-${index}`}
						href={href}
						className="inline-flex items-center rounded-md border border-white/70 bg-transparent px-3 py-1.5 text-sm xl:text-base font-medium text-white transition hover:bg-white/10"
					>
						{term.label}
					</RuntimeLink>
				);
			})}
		</div>
	);
}
